import { Component, OnInit, Output, Input, EventEmitter } from '@angular/core';
import { LookupService } from '@admin/shared/services/lookup.service';
import { StatsAndReportsService } from '../shared/services/stats-and-reports.service';
import { CustomMessageService } from '@admin/core/message.service';
import { FieldState } from '@admin/shared/models/field-state';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import {
  IValidator,
  Validator,
  IPromise,
  MessageLevel,
} from '@admin/shared/validation/validators';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { MessageType } from '@admin/core/models/enum/message-type';
import { AnnouncementMatchingService } from '../shared/services/announcement-matching.service';
import { empty } from 'rxjs';
import { AnnouncementMatchingGenerationResultModel } from '../shared/models/announcement-matching-generation-result-model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-matching-generate-on-announcement',
  templateUrl: './matching-generate-on-announcement.component.html',
  styleUrls: ['./matching-generate-on-announcement.component.less'],
  standalone: false,
})
export class MatchingGenerateOnAnnouncementComponent implements OnInit {
  @Output()
  public onAdd: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  public onClose: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  public onUpdate: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public isRegenerate: boolean;

  @Input()
  public regenerateAnnouncementId: number;

  @Input()
  public display: boolean;

  public fieldStates: { announcementId: FieldState; year: FieldState };

  public announcementId: number;

  public years: number[] = [];

  public selectedYear: number = undefined;

  public get field() {
    return this.fieldStates;
  }

  constructor(
    private lookupService: LookupService,
    private messageService: CustomMessageService,
    private translationService: TranslateService,
    private announcementMatchingService: AnnouncementMatchingService
  ) {
    let date = new Date();

    let currentYear = date.getFullYear();

    for (let i = 0; i < 7; i++) {
      this.years.push(currentYear - i);
    }
  }

  ngOnInit() {
    let lookupData = this.lookupService.getCachedLookupData();
    this.initializeFieldsState();
  }

  private initializeFieldsState() {
    this.fieldStates = {
      announcementId: new FieldState(() => this.announcementId),
      year: new FieldState(() => this.selectedYear),
    };
  }

  private invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }
    return result;
  }

  private getValidators() {
    let field = this.field;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );

    return [
      Validator.forRequired(
        field.announcementId,
        () => this.announcementId,
        emptyMessage
      ),
      Validator.forRequired(field.year, () => this.selectedYear, emptyMessage),
    ];
  }

  private getValidatorsRegenerate() {
    let field = this.field;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );

    return [
      Validator.forRequired(field.year, () => this.selectedYear, emptyMessage),
    ];
  }

  private generationResult(result: AnnouncementMatchingGenerationResultModel) {
    if (result.success) {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Success,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translationService.instant('message.SaveMessage'),
      });
      this.onUpdate.emit(this.announcementId);
      this.onClosePopUp();
    } else {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Error,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translationService.instant('message.' + result.error),
      });
    }
  }

  public onGenerateAnnouncementMatching() {
    let validators = this.getValidators();
    this.invokeValidation(validators, () => {
      this.announcementMatchingService
        .generateMatching(this.announcementId, this.selectedYear)
        .subscribe((result) => {
          this.generationResult(result);
        });
    });
  }

  public onRegenerate() {
    let regenerate = confirm(
      this.translationService.instant('message.AreYouSure')
    );
    if (regenerate) {
      let validators = this.getValidatorsRegenerate();
      this.invokeValidation(validators, () => {
        this.announcementMatchingService
          .regenerateMatching(this.regenerateAnnouncementId, this.selectedYear)
          .subscribe(
            (c) => {
              this.messageService.pushMessage(<ApplicationMessage>{
                type: MessageType.Success,
                sourceId: ApplicationMessageSource.global,
                summary: '',
                detail: this.translationService.instant('message.SaveMessage'),
              });
              this.onUpdate.emit(this.announcementId);
              this.onClosePopUp();
            },
            (err) => {
              this.messageService.pushMessage(<ApplicationMessage>{
                type: MessageType.Error,
                sourceId: ApplicationMessageSource.global,
                summary: '',
                detail: this.translationService.instant('message.Error'),
              });
            }
          );
      });
    }
  }

  public onGenerate() {
    this.onAdd.emit(true);
  }

  public onClosePopUp() {
    this.announcementId = null;
    this.initializeFieldsState();
    this.onClose.emit(false);
  }
}
